import { createRouter, createWebHistory } from 'vue-router'
import MainPage from '../components/01main/MainPage.vue'
import YangiliklarElonlar from '../components/02yangiliklar_va_elonlar/YangiliklarElonlar.vue'
import { URL_LANGS, langForLocale, localeForLang, setLocale } from '@/i18n'

// Every page sits under a language prefix — /uz/…, /ru/…, /en/… — so each piece
// of content has one shareable link per language. `langRoutes` below holds the
// whole site with paths RELATIVE to that prefix.
const LANG_MATCH = URL_LANGS.join('|')

// Redirect helper for section landing pages: keeps the caller's language.
const toLang = (path) => (to) => `/${to.params.lang}${path}`

const langRoutes = [
  {
    path: '',
    name: 'home',
    component: MainPage
  },
  {
    path: 'yangiliklar-va-elonlar',
    name: 'yangiliklar-va-elonlar',
    component: YangiliklarElonlar
  },

  // Markaz — one persistent layout (banner + side-nav); children swap content only
  {
    path: 'markaz',
    component: () => import('@/components/common/SectionLayout.vue'),
    children: [
      { path: '', redirect: toLang('/markaz/markaz-haqida') },
      { path: 'markaz-haqida', name: 'markaz-haqida', component: () => import('../PAGES/01markaz/MarkazHaqdia.vue'), meta: { section: 'markaz', titleKey: 'nav.markazHaqida' } },
      { path: 'rahbariyat', name: 'rahbariyat', component: () => import('../PAGES/01markaz/PeopleListPage.vue'), props: { storeModule: 'leadership' }, meta: { section: 'markaz', titleKey: 'nav.rahbariyat' } },
      { path: 'boshqarma-boshliqlari', name: 'boshqarma-boshliqlari', component: () => import('../PAGES/01markaz/PeopleListPage.vue'), props: { storeModule: 'departments' }, meta: { section: 'markaz', titleKey: 'nav.boshqarmaBoshliqlari' } },
      { path: 'tuzilma', name: 'tuzilma', component: () => import('../PAGES/01markaz/TuzilmaPart.vue'), meta: { section: 'markaz', titleKey: 'nav.tuzilma' } }
    ]
  },

  // Faoliyat — persistent layout (banner + side-nav); children swap content only
  {
    path: 'faoliyat',
    component: () => import('@/components/common/SectionLayout.vue'),
    children: [
      { path: '', redirect: toLang('/faoliyat/oquv-metodik') },
      { path: 'oquv-metodik', name: 'oquv-metodik', component: () => import('../PAGES/02faoliyat/FaoliyatText.vue'), props: { blocksKey: 'pages.oquvMetodik.blocks' }, meta: { section: 'faoliyat', titleKey: 'nav.oquvMetodik' } },
      { path: 'baholash-monitoring', name: 'baholash-monitoring', component: () => import('../PAGES/02faoliyat/FaoliyatText.vue'), props: { blocksKey: 'pages.baholash.blocks' }, meta: { section: 'faoliyat', titleKey: 'nav.baholashMonitoring' } },
      { path: 'raqamli-savodxonlik', name: 'raqamli-savodxonlik', component: () => import('../PAGES/02faoliyat/FaoliyatText.vue'), props: { blocksKey: 'pages.raqamli.blocks' }, meta: { section: 'faoliyat', titleKey: 'nav.raqamliSavodxonlik' } },
      { path: 'talim-normativlari', name: 'talim-normativlari', component: () => import('../PAGES/02faoliyat/FaoliyatText.vue'), props: { blocksKey: 'pages.normativ.blocks' }, meta: { section: 'faoliyat', titleKey: 'nav.talimNormativlari' } },
      // Hisobotlar: a report reads inside the section layout too, so the side-nav
      // stays put while moving between the list and a report.
      { path: 'hisobotlar', name: 'hisobotlar', component: () => import('../PAGES/02faoliyat/HisobotlarList.vue'), meta: { section: 'faoliyat', titleKey: 'nav.hisobotlar' } },
      { path: 'hisobotlar/:id(\\d+)', name: 'hisobot-detail', component: () => import('../PAGES/02faoliyat/HisobotDetail.vue'), meta: { section: 'faoliyat', titleKey: 'nav.hisobotlar' } }
    ]
  },

  // Ilmiy-metodik faoliyat va xalqaro hamkorlik — reached from the Faoliyat
  // dropdown, but its own section (own side-nav). Yosh olimlar and Ilmiy
  // maqolalar moved here from Axborot xizmati; the rest are placeholders.
  {
    path: 'ilmiy-metodik',
    component: () => import('@/components/common/SectionLayout.vue'),
    children: [
      { path: '', redirect: toLang('/ilmiy-metodik/faoliyat') },
      { path: 'faoliyat', name: 'ilmiy-metodik-faoliyat', component: () => import('../PAGES/06ilmiy/ComingSoon.vue'), meta: { section: 'ilmiy', titleKey: 'nav.ilmiyMetodikFaoliyat' } },
      { path: 'xalqaro-hamkorlik', name: 'xalqaro-hamkorlik', component: () => import('../PAGES/06ilmiy/ComingSoon.vue'), meta: { section: 'ilmiy', titleKey: 'nav.xalqaroHamkorlik' } },
      { path: 'yosh-olimlar', name: 'yosh-olimlar', component: () => import('../PAGES/04axborot/YoshOlimlar.vue'), meta: { section: 'ilmiy', titleKey: 'nav.yoshOlimlar' } },
      // The filterable/badge design (IlmiyMaqolalar2) replaced the plain table
      // (IlmiyMaqolalar.vue, kept unrouted for now).
      { path: 'ilmiy-maqolalar', name: 'ilmiy-maqolalar', component: () => import('../PAGES/04axborot/IlmiyMaqolalar2.vue'), meta: { section: 'ilmiy', titleKey: 'nav.ilmiyMaqolalar' } },
      { path: 'ilmiy-loyihalar', name: 'ilmiy-loyihalar', component: () => import('../PAGES/06ilmiy/ComingSoon.vue'), meta: { section: 'ilmiy', titleKey: 'nav.ilmiyLoyihalar' } }
    ]
  },
  // Detail page keeps its own full layout (opened from within content)
  { path: 'ilmiy-metodik/yosh-olimlar/:slug', name: 'yosh-olimlar-detail', component: () => import('../PAGES/04axborot/YoshOlimlarDetail.vue') },

  // Hujjatlar (one shared page, parameterised by document category)
  { path: 'hujjatlar/prezident-farmonlari', name: 'prezident-farmonlari', component: () => import('../PAGES/03hujjatlar/HujjatlarPage.vue'), props: { category: 'president_decrees', titleKey: 'nav.prezidentFarmonlari' } },
  { path: 'hujjatlar/talimga-oid', name: 'talimga-oid', component: () => import('../PAGES/03hujjatlar/HujjatlarPage.vue'), props: { category: 'education_laws', titleKey: 'nav.talimgaOidQonunlar' } },
  { path: 'hujjatlar/vazirlar-mahkamasi', name: 'vazirlar-mahkamasi', component: () => import('../PAGES/03hujjatlar/HujjatlarPage.vue'), props: { category: 'cabinet_decisions', titleKey: 'nav.vazirlarMahkamasi' } },
  { path: 'hujjatlar/markaz-faoliyati', name: 'markaz-faoliyati', component: () => import('../PAGES/03hujjatlar/HujjatlarPage.vue'), props: { category: 'center_docs', titleKey: 'nav.markazFaoliyati' } },

  // Axborot xizmati — persistent layout for side-nav pages; detail pages stay flat
  {
    path: 'axborot',
    component: () => import('@/components/common/SectionLayout.vue'),
    children: [
      { path: '', redirect: toLang('/axborot/savol-javob') },
      { path: 'savol-javob', name: 'savol-javob', component: () => import('../PAGES/04axborot/SavolJavob.vue'), meta: { section: 'axborot', titleKey: 'nav.savolJavob' } },
      { path: 'ochiq-malumotlar', name: 'ochiq-malumotlar', component: () => import('../PAGES/04axborot/OchiqMalumotlar.vue'), meta: { section: 'axborot', titleKey: 'nav.ochiqMalumotlar' } },
      { path: 'yangiliklar', name: 'yangiliklar', component: () => import('../PAGES/04axborot/YangiliklarPart.vue'), meta: { section: 'axborot', titleKey: 'nav.yangiliklar' } },
      { path: 'galereya', name: 'galereya', component: () => import('../PAGES/04axborot/GalereyaPart.vue'), meta: { section: 'axborot', titleKey: 'nav.galereya' } },
      // Moved to Ilmiy-metodik faoliyat; old (and shared) links keep working.
      { path: 'yosh-olimlar', redirect: toLang('/ilmiy-metodik/yosh-olimlar') },
      { path: 'ilmiy-maqolalar', redirect: toLang('/ilmiy-metodik/ilmiy-maqolalar') },
      { path: 'ilmiy-maqolalar2', redirect: toLang('/ilmiy-metodik/ilmiy-maqolalar') }
    ]
  },
  // Axborot detail pages keep their own full layout (opened from within content)
  { path: 'axborot/yosh-olimlar/:slug', redirect: (to) => `/${to.params.lang}/ilmiy-metodik/yosh-olimlar/${to.params.slug}` },
  { path: 'axborot/yangiliklar/:id', name: 'yangiliklar-detail', component: () => import('../PAGES/04axborot/YangiliklarDetail.vue') },

  // Bogʻlanish
  { path: 'boglanish', name: 'boglanish', component: () => import("../PAGES/05bog'lanish/BoglanishPart.vue") },

  // PISA 2025 — Oʻzbekiston natijalari (standalone infographic page; trm.uz/pisa → /uz/pisa)
  { path: 'pisa', name: 'pisa', component: () => import('../PAGES/pisa/PisaPage.vue') },

  // Unknown paths under a valid language fall back to that language's home page.
  { path: ':pathMatch(.*)*', redirect: toLang('') }
]

const routes = [
  {
    // No component: vue-router skips component-less records when resolving which
    // depth a <router-view> renders, so this purely carries the :lang param and
    // the page layouts stay exactly where they were.
    path: `/:lang(${LANG_MATCH})`,
    children: langRoutes
  },
  {
    // Links written before the prefixes existed — and bookmarks, and anything
    // shared in the old format — get sent to the same page in the reader's own
    // language instead of 404-ing.
    path: '/:pathMatch(.*)*',
    redirect: (to) => `/${langForLocale()}${to.fullPath === '/' ? '' : to.fullPath}`
  }
]

// ⚠️ DO NOT CHANGE — scroll-to-top-on-refresh (3 of 3, the main logic).
// Disable the browser's native scroll restoration so a page refresh (or a
// direct visit to a deep-linked URL) always starts at the top instead of where
// the user last was. Navigation scrolling is handled by scrollBehavior below.
// This pairs with the same line in main.js and the window.scrollTo fallback in
// App.vue. The three together are what make "refresh → top" work; touching this
// (or the isFreshLoad branch below) reintroduces refresh-keeps-old-position.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual'
}

// True only for the very first navigation of this page load, so a refresh /
// deep link can be forced to the top while later in-app back/forward keeps its
// restored scroll position. Reset every reload because the module re-evaluates.
// ⚠️ Part of scroll-to-top-on-refresh — see the warning above; do not remove.
let isFreshLoad = true

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  // Scroll to the top on navigation, but honour the saved position when the
  // user presses the browser back/forward buttons. The short delay lets the
  // page cross-fade transition finish so the reset lands on the new page.
  scrollBehavior(to, from, savedPosition) {
    // First navigation after a page load (i.e. a refresh or a direct deep link):
    // always start at the top, ignoring the scroll position vue-router restores
    // from history.state across reloads.
    if (isFreshLoad) {
      isFreshLoad = false
      if (to.hash) return { el: to.hash, behavior: 'smooth' }
      return { top: 0, left: 0 }
    }
    if (savedPosition) return savedPosition
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return new Promise((resolve) => {
      setTimeout(() => resolve({ top: 0, left: 0 }), 320)
    })
  }
})

// The URL is the source of truth for the language: opening /ru/… shows Russian
// even if the last visit was in Uzbek. It is not persisted (see setLocale) —
// reading one shared link must not rewrite the visitor's own preference.
router.beforeEach((to) => {
  if (to.params.lang) setLocale(localeForLang(to.params.lang), { persist: false })
})

export default router
